import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getRobotList, selectRobotList } from "@/store/ses/sesRobotSlice";
import { getEndpointList, selectEndpointList } from "@/store/ses/sesEndpointSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import { getDaysInMonth } from "@/helpers";
import { IEndpoint } from "@/types/ses/sesItemConfigTypes";

import {
	useDeleteCallingListMutation,
	useGetCallingListsQuery,
	useGetListInstanceQuery,
	useGetListJobsQuery,
	useGetTimeintervalsQuery,
	usePauseJobsMutation,
	useResumeJobsMutation,
	// useStartCallingListMutation,
	// useStopCallingListMutation,
	useUpdateCallingListMutation,
} from "@/store/RTKQuery/clsSlice";

import { useFormik } from "formik";
import type { FormikState } from "formik";
import { initialValues, parseCron, stringifyCron } from "./CallingLineParams.const";
import { validationSchema } from "./CallingLineParams.validation";
import { IRobotPhoneDestination, TinitialValues } from "./CallingLineParams.props";
import { selectCurrentUserInfo } from "@/store/userSlice";

export const useCallingLineParams = () => {
	// служебные
	const dispatch = useAppDispatch();
	const translate = useTranslate();
	const isAccess = useAccessRight();

	// cookies и режимы отображения
	const [cookies, setCookie] = useCookies(["cls"]);
	const isInitialized = useRef(false);
	const robotList = useAppSelector(selectRobotList); // список роботов
	const globalEndpointsData = useAppSelector(selectEndpointList);

	// RTKquery
	const {
		data: callingListsData,
		isLoading: isCallingListsLoading,
		refetch: refetchCallingLists,
	} = useGetCallingListsQuery({}, { refetchOnMountOrArgChange: false, refetchOnReconnect: false });

	const currentCallingListId = cookies?.cls && cookies.cls.displayMode ? JSON.parse(cookies.cls.displayMode).selectedCallList : null;

	const { data: timeintervalsList, isLoading: isTimeintervalsListLoading } = useGetTimeintervalsQuery(undefined);
	const { data: jobsList, isLoading: isJobsListLoading, refetch: refetchJobsList } = useGetListJobsQuery({ id: currentCallingListId });
	const { data: _, isLoading: isProcessDataLoading } = useGetListInstanceQuery({ id: currentCallingListId ?? "" });

	const [updateCallingList] = useUpdateCallingListMutation();
	const [resumeJobs] = useResumeJobsMutation();
	const [pauseJobs] = usePauseJobsMutation();
	// const [startCallingList] = useStartCallingListMutation();
	// const [stopCallingList] = useStopCallingListMutation();
	const [deleteCallingList] = useDeleteCallingListMutation();
	const currentUserInfo = useAppSelector(selectCurrentUserInfo); // store - информация о текущем пользователе

	const [showAlertDialog, setShowAlertDialog] = useState<boolean>(false);
	const [showNotification, setShowNotification] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [endpointsList, setEndpointsList] = useState<{ label: string; value: string }[]>([]);
	const [localEndpointsData, setLocalEndpointsData] = useState<IEndpoint[]>(globalEndpointsData.data ?? []);
	const [currentChannel, setCurrentChannel] = useState<"voice" | "telegram" | "">("");
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [displayMode, setDisplayMode] = useState<{ selectedCallList: string; currentChannel: string }>({
		selectedCallList: "",
		currentChannel: "",
	});
	const [warningCounter, setWarningCounter] = useState(0);
	const [robotPhoneDestinationList, setRobotPhoneDestinationList] = useState<IRobotPhoneDestination[]>([]);
	const [autocompleteOptionsForSource, setAutocompleteOptionsForSource] = useState<
		{
			value: string | number;
			label: string;
		}[]
	>([]);

	useEffect(() => {
		if (robotList && robotList.status === "idle") {
			const robotPhoneDestinations = robotList.data.reduce<IRobotPhoneDestination[]>((acc, el: any) => {
				const destinations = el.destinations;

				if (destinations && Array.isArray(destinations)) {
					return [
						...acc,
						{
							robot: el.name,
							destinations: [...destinations],
						},
					];
				} else {
					return [
						...acc,
						{
							robot: el.name,
							destinations: [],
						},
					];
				}
			}, []);
			setRobotPhoneDestinationList(robotPhoneDestinations);
		}
	}, [robotList]);

	useEffect(() => {
		const normalizedDestinations = robotPhoneDestinationList.reduce<
			{
				value: string | number;
				label: string;
			}[]
		>((acc, el) => {
			const destinations = el.destinations;
			const result = destinations.map((item) => ({ label: `${item} (${el.robot})`, value: `${item}` }));
			return [...acc, ...result];
		}, []);
		setAutocompleteOptionsForSource(normalizedDestinations);
	}, [robotPhoneDestinationList]);

	const isListActive = useMemo(() => {
		if (!jobsList?.jobs?.length) return false;
		return jobsList.jobs.every((el) => !el.paused);
	}, [jobsList]);

	useEffect(() => {
		refetchCallingLists();
	}, [currentUserInfo.dataset]);

	const currentCallingList = useMemo(() => {
		if (!callingListsData || !Array.isArray(callingListsData?.lists)) return undefined;
		return callingListsData.lists.find((el) => el.id === currentCallingListId);
	}, [currentCallingListId, callingListsData, currentUserInfo.dataset]);

	// форма
	const formik = useFormik({
		initialValues: initialValues,
		validateOnMount: false,
		validateOnChange: false,
		validateOnBlur: true,
		validationSchema: validationSchema,
		onSubmit: (values) => {
			const currentCallingList = callingListsData?.lists.find((el) => el.id === currentCallingListId);
			if (!currentCallingList) return;
			handleShowNotice("noticeSaving_inProgress");
			setIsSubmitting(true);

			const payload =
				currentChannel === "telegram"
					? {
							name: values.name,
							source: values.source,
							description: values.description,
							channel_parameters: {
								robot_id: values.robotId,
								endpoint: (endpointsList.map((el) => el.value).includes(values.endpointId as string) ? values.endpointId : "-") ?? "-",
							},
							scheduler: values.scheduler.map(stringifyCron),
							autoload: values.autoload,
							timeintervals: values.timeintervals,
					  }
					: {
							source: values.source,
							name: values.name,
							description: values.description,
							limits: {
								no_answer_timeout: values.noAnswerTimeout,
								dial_tries: values.dialTries,
								max_channels: values.maxChannels,
								redial_timeout: values.redialTimeout,
								watchdog: values.watchdog,
								retry_causes: values.retryCauses,
							},
							scheduler: values.scheduler.map(stringifyCron),
							autoload: values.autoload,
							timeintervals: values.timeintervals,
							trunk: values.trunk,
					  };

			updateCallingList({
				original: currentCallingList,
				changes: payload,
			})
				.unwrap()
				.then((res) => {
					if (res.error === 0) {
						handleShowNotice("noticeSaving_success");
						values.isListActiveLocal && resumeJobs({ id: currentCallingListId });
						!values.isListActiveLocal && pauseJobs({ id: currentCallingListId });
						formik.resetForm({ values: { ...values, endpointId: values.endpointId === "-" ? translate("title_notFound") : values.endpointId } });
					} else if (res.error === 1) {
						handleShowNotice(`${translate("noticeSaving_failed")}: ${res.message}`);
					}
				})
				.catch((err) => {
					handleShowNotice(`${translate("noticeSaving_failed")}: ${JSON.stringify(err)}`);
				})
				.finally(() => setIsSubmitting(false));
		},
	});

	// показ уведомления с сообщением
	const handleShowNotice = (message: string) => {
		setSnackbarMessage(message);
		setShowNotification(true);
	};

	// получаем список роботов
	useEffect(() => {
		isAccess(SES.ROBOT_LIST) && dispatch(getRobotList());
	}, []);

	useEffect(() => {
		if (formik.values.robotId && isAccess(SES.ENDPOINT_LIST)) {
			dispatch(getEndpointList(formik.values.robotId))
				.unwrap()
				.then((res) => {
					setLocalEndpointsData(Array.isArray(res) ? res : []);
				})
				.catch(() => setLocalEndpointsData([]));
		}
	}, [formik.values.robotId, currentUserInfo.dataset]);

	// делаем локальный список эндпоинтов
	useEffect(() => {
		const manualEndpoints = (localEndpointsData || []).filter((el) => el.entryType === "manual");
		if (manualEndpoints.length > 0) {
			setEndpointsList(manualEndpoints.map((endpoint) => ({ label: endpoint.name, value: endpoint.id })));
		} else {
			setEndpointsList([]);
			formik.setFieldValue("endpointId", translate("title_notFound"));
		}
	}, [localEndpointsData, currentUserInfo.dataset]);

	// следим за режимом отображения
	useEffect(() => {
		if (!displayMode.selectedCallList || !displayMode.currentChannel) return;
		setCookie("cls", {
			...cookies["cls"],
			displayMode: JSON.stringify(displayMode),
		});
	}, [displayMode, currentUserInfo.dataset]);

	// когда лист подгружается - вставляем в форму его параметры
	useEffect(() => {
		if (callingListsData?.error === 1) return;

		const current = callingListsData?.lists?.find((el) => el.id === currentCallingListId);
		if (current) {
			const newValues: TinitialValues = {
				name: current.name,
				source: current.source,
				noAnswerTimeout: current.limits?.no_answer_timeout ? current.limits.no_answer_timeout : 0,
				dialTries: current.limits?.dial_tries ? current.limits.dial_tries : 0,
				description: current.description,
				maxChannels: current.limits?.max_channels ? current.limits.max_channels : 0,
				redialTimeout: current.limits?.redial_timeout ? current.limits.redial_timeout : 0,
				watchdog: current.limits?.redial_timeout ? current.limits.watchdog : 0,
				retryCauses: current.limits?.retry_causes ? current.limits.retry_causes : [],
				robotId: current.channel_parameters?.robot_id || "",
				endpointId: current.channel_parameters?.endpoint || "",
				timeintervals: current.timeintervals,
				autoload: current.autoload ?? false,
				scheduler: (current.scheduler || ["0 0 1 1 *"]).map(parseCron),
				isListActiveLocal: isListActive ?? false,
				trunk: current.trunk ?? "",
			};
			formik.resetForm({
				values: newValues,
				initialValues: newValues,
				validateOnMount: false,
			} as Partial<FormikState<TinitialValues>> & { values: TinitialValues; initialValues: TinitialValues });

			setCurrentChannel(current.channel as "voice" | "telegram" | "");
			isInitialized.current = true;
		}
	}, [callingListsData, currentCallingListId, isListActive, currentUserInfo.dataset]);

	// перезапрашиваем список работ при смене листа
	useEffect(() => {
		refetchJobsList();
	}, [currentCallingListId, currentUserInfo.dataset]);

	// Запуск обзвона текущего листа
	// const handleStartCallingForCurrentList = () => {
	// 	if (!currentCallingListId) return;
	// 	handleShowNotice("notice_callListRunning");
	// 	setIsSubmitting(true);
	// 	startCallingList({ listId: currentCallingListId })
	// 		.unwrap()
	// 		.then((res) => (res.error === 0 ? handleShowNotice("notice_callingListStarted") : handleShowNotice(`${translate("notice_callingListStartError")}: ${res.message}`)))
	// 		.catch((err) => handleShowNotice(`${translate("notice_callingListStartError")}: ${JSON.stringify(err)}`))
	// 		.finally(() => setIsSubmitting(false));
	// };

	// обработчик остановки обзвона
	// const handleStopCallingForCurrentList = () => {
	// 	if (!currentCallingListId) return;
	// 	handleShowNotice("notice_callListStopping");
	// 	setIsSubmitting(true);
	// 	stopCallingList({ listId: currentCallingListId })
	// 		.unwrap()
	// 		.then((res) =>
	// 			res.error === 0 ? handleShowNotice("notice_callingListStoped") : handleShowNotice(`${translate("notice_callingListStopError")}: ${JSON.stringify(res.message)}`)
	// 		)
	// 		.catch((err) => handleShowNotice(`${translate("notice_callingListStopError")}: ${JSON.stringify(err)}`))
	// 		.finally(() => setIsSubmitting(false));
	// };

	// обработчик удаления листа
	const handleDeleteCallingList = () => {
		if (!callingListsData) return;

		if (warningCounter === 1) {
			setIsSubmitting(true);
			handleShowNotice("notice_deletionInProgress");
			deleteCallingList({ listId: currentCallingListId })
				.unwrap()
				.then((res) => {
					if (res.error === 0) {
						handleShowNotice("notice_deletionSuccess");
						const updatedLists = callingListsData.lists.filter((list) => list.id !== currentCallingListId);

						const newSelected = updatedLists.sort((a, b) => a.name.localeCompare(b.name))[0];

						if (newSelected) {
							setDisplayMode({
								selectedCallList: newSelected.id,
								currentChannel: newSelected.channel,
							});
						} else {
							setDisplayMode({ selectedCallList: "", currentChannel: "" });
						}
					} else if (res.error === 1) {
						handleShowNotice(`${translate("notice_deletionError")}: ${JSON.stringify(res.message)}`);
					}
				})
				.catch((err) => handleShowNotice(`${translate("notice_deletionError")}: ${JSON.stringify(err)}`))
				.finally(() => {
					setIsSubmitting(false);
					setWarningCounter(0);
				});
		} else {
			setShowAlertDialog(true);
			setWarningCounter((prev) => prev + 1);
		}
	};

	// сохраненние данных
	const handleSubmitForm = () => {
		if (!isInitialized.current) return;
		if (!formik.dirty) return;
		formik.submitForm();
	};

	const toggleListActive = () => {
		formik.setValues((prev) => ({ ...prev, isListActiveLocal: !formik.values.isListActiveLocal }));
	};
	useEffect(() => {
		if (warningCounter < 0) {
			setWarningCounter(0);
		}
	}, [warningCounter]);

	// переключатель модального окна
	const toggleAlert = (warningCounterValue: number): Dispatch<SetStateAction<boolean>> => {
		return (value) => {
			setShowAlertDialog(value);

			if (warningCounterValue === 1) {
				setTimeout(() => {
					if (warningCounter > 0) {
						setWarningCounter((prev) => prev - 1);
					}
				}, 500);
			}
		};
	};

	// получение списка дней
	const getDayVariantsForMonth = (monthValue: string | number) => {
		const monthIndex = monthValue === "*" ? 0 : Number(monthValue);
		return [{ title: "*", value: "*" }, ...getDaysInMonth(monthIndex)];
	};

	// получение глобального состояния загрузки
	const isLoading = isCallingListsLoading || isTimeintervalsListLoading || isJobsListLoading || isProcessDataLoading;

	return {
		// data
		callingListsData,
		currentCallingListId,
		currentCallingList,
		jobsList,
		// processData,
		timeintervalsList,
		robotList,
		globalEndpointsData,
		localEndpointsData,
		endpointsList,
		autocompleteOptionsForSource,

		// formik
		formik,

		// ui
		showAlertDialog,
		setShowAlertDialog,
		showNotification,
		setShowNotification,
		isSubmitting,
		setIsSubmitting,
		snackbarMessage,
		setSnackbarMessage,
		displayMode,
		setDisplayMode,
		warningCounter,
		setWarningCounter,
		currentChannel,
		setCurrentChannel,

		// обработчики
		handleShowNotice,
		// handleStartCallingForCurrentList,
		// handleStopCallingForCurrentList,
		handleDeleteCallingList,
		toggleListActive,
		toggleAlert,
		getDayVariantsForMonth,
		handleSubmitForm,

		//служебные
		isAccess,
		translate,
		isListActive,
		isLoading,
		refetchCallingLists,
		refetchJobsList,
	};
};
